import type { GroupChildLayer, Layer, LayerId, PieceModel } from './types';

// Walks top-level layers and (recursively) group children, returning the first match.
const find = (layers: Layer[], match: (layer: Layer) => boolean): Layer | null => {
  for (const layer of layers) {
    if (match(layer)) return layer;
    if (layer.kind === 'group') {
      const found = find(layer.content.children, match);
      if (found) return found;
    }
  }
  return null;
};

/** The layer with this id (top-level or nested group child), or null. */
export const findLayerById = (model: PieceModel, id: LayerId): Layer | null =>
  find(Object.values(model.layers), layer => layer.id === id);

/** The layer with this pieceId, or null. First match wins, like buildPieceIdIndex. */
export const findLayerByPieceId = (model: PieceModel, pieceId: string): Layer | null =>
  find(Object.values(model.layers), layer => layer.pieceId === pieceId);

const updateInSubtree = (layer: Layer, id: LayerId, updater: (l: Layer) => Layer): Layer | null => {
  if (layer.id === id) return updater(layer);
  if (layer.kind === 'group') {
    let changed = false;
    const children = layer.content.children.map(child => {
      const updated = updateInSubtree(child, id, updater);
      if (updated) {
        changed = true;
        return updated as GroupChildLayer;
      }
      return child;
    });
    if (changed) return { ...layer, content: { ...layer.content, children } };
  }
  return null;
};

/** Applies `updater` to the layer matching `id` (top-level or nested group child), immutably. */
export const updateLayerById = (model: PieceModel, id: LayerId, updater: (layer: Layer) => Layer): PieceModel => {
  for (const [key, layer] of Object.entries(model.layers)) {
    const updated = updateInSubtree(layer, id, updater);
    if (updated) return { ...model, layers: { ...model.layers, [key]: updated } };
  }
  return model;
};
